import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { confirmPopup } from "../utils/Popups";
import { getToken } from "../utils/Auth";
import { API_BASE } from "../utils/Constants";
import { handleBulkSorDownloadProcess, formatBytes } from "../utils/commonFunction";
import streamSaver, { createWriteStream } from "streamsaver";

// Configure streamSaver mitm for global usage
streamSaver.mitm = `${window.location.origin}/streamsaver/mitm.html`;

export const useBulkSorDownload = () => {
    const [bulkPdfDownloading, setBulkPdfDownloading] = useState(false);
    const [isSoRDownloading, setIsSoRDownloading] = useState(false);
    const [soRPhase, setSoRPhase] = useState('generating');
    const [soRProgress, setSoRProgress] = useState(0);
    const [soRProcessedCount, setSoRProcessedCount] = useState(0);
    const [soRTotalCount, setSoRTotalCount] = useState(0);

    // Effect to handle body class when modal is open, replacing jQuery
    useEffect(() => {
        if (isSoRDownloading) {
            document.body.classList.add('swal2-shown', 'swal2-height-auto');
        } else {
            document.body.classList.remove('swal2-shown', 'swal2-height-auto');
        }
        return () => {
            document.body.classList.remove('swal2-shown', 'swal2-height-auto');
        };
    }, [isSoRDownloading]);

    const startDownload = async (payload, options = {}) => {
        const { onCleanupCallback } = options;

        setBulkPdfDownloading(true);
        setSoRPhase('generating');
        setSoRProgress(0);
        setSoRProcessedCount(0);
        setSoRTotalCount(0);
        setIsSoRDownloading(true);

        const onCleanup = () => {
            if (onCleanupCallback) onCleanupCallback();
        };

        const onProgress = (processed, total, pct) => {
            setSoRProcessedCount(processed);
            setSoRTotalCount(total);
            setSoRProgress(pct);
        };

        const onSuccess = async (jobId, fileName) => {
            setSoRPhase('downloading');
            setSoRProcessedCount(0);
            setSoRTotalCount(0);
            setSoRProgress(0);

            let writer = null;
            try {
                const isSingle = payload && payload.student_id ? true : false;
                const apiUrl = `/downloadBulkSorZip/${jobId}`;
                const finalZipName = fileName || (isSingle ? 'SOR.pdf' : 'SOR.zip');

                const response = await fetch(`${API_BASE}${apiUrl}`, {
                    method: 'GET',
                    headers: { 'Authorization': `Bearer ${getToken()}` },
                });

                if (!response.ok) throw new Error('Network response was not ok');

                const fileStream = createWriteStream(finalZipName);

                let totalFileSize = response.headers.get('content-length') || 0;
                setSoRTotalCount(totalFileSize);

                const throttledProgress = (() => {
                    let lastCall = 0;
                    return (loaded) => {
                        const now = Date.now();
                        if (now - lastCall >= 100) {
                            setSoRProcessedCount(loaded);
                            if (totalFileSize > 0) {
                                setSoRProgress(Math.round((loaded / totalFileSize) * 100));
                            }
                            lastCall = now;
                        }
                    };
                })();

                const reader = response.body.getReader();
                writer = fileStream.getWriter();
                let received = 0;

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    await writer.write(value);
                    received += value.length;
                    throttledProgress(received);
                }

                setSoRProcessedCount(totalFileSize);
                setSoRProgress(100);
                await new Promise(resolve => setTimeout(resolve, 100));
                await writer.close();
            } catch (error) {
                if (writer) {
                    try {
                        await writer.abort(error);
                    } catch (abortError) {
                        console.warn("Failed to abort stream writer cleanly:", abortError);
                    }
                }
                console.error("Download failed:", error);
                Swal.fire({ icon: "error", title: "Download Failed", text: "Failed to download.. try again" });
            } finally {
                setBulkPdfDownloading(false);
                setIsSoRDownloading(false);
            }
        };

        const onError = (errInfo) => {
            setBulkPdfDownloading(false);
            setIsSoRDownloading(false);
            Swal.fire("Error", errInfo || "Unknown", "error");
        };

        handleBulkSorDownloadProcess(payload, onCleanup, onProgress, onSuccess, onError);
    };

    const SorDownloadProgressUI = () => (
        isSoRDownloading ? (
            <>
                <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
                <div className="downloading-progress modal fade show d-block" id="downloadSoRProgress" tabIndex="-1" role="dialog" aria-hidden="true" data-backdrop="static" style={{ zIndex: 1050 }}>
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content border-0 shadow" style={{ borderRadius: '5px' }}>
                            <div className="modal-body p-4">
                                <div className="swal2-header border-0">
                                    <div className="swal2-icon swal2-info swal2-icon-show d-flex mb-4">
                                        <div className="swal2-icon-content">i</div>
                                    </div>
                                    <h2 className="swal2-title d-flex justify-content-center mb-4">{soRPhase === 'generating' ? 'Generating PDFs...' : 'Downloading...'}</h2>
                                    <div className="progress w-100 mb-2">
                                        <div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" aria-valuenow={soRProgress} aria-valuemin="0" aria-valuemax="100" style={{ width: `${soRProgress}%` }}>
                                            {soRProgress}%
                                        </div>
                                    </div>
                                    <div className="text-center mt-2">
                                        {soRPhase === 'generating'
                                            ? `Processed: ${soRProcessedCount} / ${soRTotalCount}`
                                            : `File Size: ${formatBytes(soRProcessedCount, 1)} / ${formatBytes(soRTotalCount, 1) || 'Unknown'}`}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        ) : null
    );

    return {
        bulkPdfDownloading,
        setBulkPdfDownloading,
        startDownload,
        SorDownloadProgressUI
    };
};

export const confirmAddToProfile = (selectedStudentsCount, onConfirm) => {
    confirmPopup({
      title: 'Add to Student Profile?',
      text: `This will save a Statement of Results for ${selectedStudentsCount} student${selectedStudentsCount > 1 ? 's' : ''} and send them a notification.\n\nPlease ensure you have checked that the results are correct before proceeding.`,
      icon: 'warning',
      confirmButtonText: 'Yes, proceed!'
    }, (result) => {
      if (result.isConfirmed) {
        if (typeof onConfirm === 'function') onConfirm();
      }
    });
};
